import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * Guard wrapper.
 *  - `customerOnly=true`  → only customers can pass; everyone else is sent to /app
 *  - default              → only business accounts pass; customers are sent to /portal
 */
export default function RequireAuth({ children, customerOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // If we have a hydrated user from cache, render immediately so the
  // installed PWA opens in well under a second. `/auth/me` keeps
  // refreshing in the background.
  if (loading && !user) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) {
    // Preserve the path the user was trying to reach so they return here
    // after login (critical for the Staff Notes installed PWA flow).
    const next = location.pathname + (location.search || "");
    const to = `/login?next=${encodeURIComponent(next)}`;
    return <Navigate to={to} replace />;
  }
  const accountType = user.account_type || user.role;
  if (accountType === "platform_admin") return <Navigate to="/admin" replace />;
  if (accountType === "promoter") return <Navigate to="/promoter" replace />;
  if (customerOnly && accountType !== "customer") {
    return <Navigate to="/app" replace />;
  }
  if (!customerOnly && accountType === "customer") {
    return <Navigate to="/portal" replace />;
  }
  return children;
}
